import { useMemo, useState } from "react";
import { useEvents, useUpdateEvent } from "../../api/hooks";
import type { CalEvent, EventInput, Holiday } from "../../api/types";
import { HOLIDAY_COLOR } from "../../lib/colors";
import { absMinutes, addDays, datePart, fromAbsMinutes, longDateLabel, type DateStr } from "../../lib/date";
import { useUi, type Anchor } from "../../state/ui";
import { Popover } from "../ui/Popover";

/**
 * Events of [from, to) restricted to visible calendars, plus holidays. The agenda views also
 * apply the category filter; the home page passes `byCategory: false` to see the whole day.
 */
export function useVisibleEvents(from: DateStr, to: DateStr, { byCategory = true } = {}) {
  const ui = useUi();
  const query = useEvents(from, to);
  const visible = useMemo(() => new Set(ui.calendars.filter((c) => c.isVisible).map((c) => c.id)), [ui.calendars]);
  const categories = useMemo(() => (byCategory && ui.categoryFilter.length ? new Set(ui.categoryFilter) : null), [byCategory, ui.categoryFilter]);
  const events = useMemo(
    () =>
      (query.data?.events ?? []).filter(
        (e) => (!e.calendarId || visible.has(e.calendarId)) && (!categories || (e.category !== null && categories.has(e.category)))
      ),
    [query.data, visible, categories]
  );
  return { events, holidays: query.data?.holidays ?? [], loading: query.isLoading };
}

/** Exclusive end day of an event (all-day ends are already exclusive dates). */
export function endDay(e: CalEvent): DateStr {
  if (e.allDay) return e.end;
  // A timed event ending at 00:00 doesn't occupy that day.
  return e.end.endsWith("T00:00") ? datePart(e.end) : addDays(datePart(e.end), 1);
}

/** All-day, or timed but 24h or longer: shown in the all-day strip / as month bars. */
export function isBarEvent(e: CalEvent): boolean {
  return e.allDay || absMinutes(e.end) - absMinutes(e.start) >= 1440;
}

export function isPast(e: CalEvent, today: DateStr, nowMinutes: number): boolean {
  const nowAbs = absMinutes(`${today}T00:00`) + nowMinutes;
  return absMinutes(e.allDay ? `${e.end}T00:00` : e.end) <= nowAbs;
}

/** Moves an event by `deltaMinutes` (time grid) or `deltaDays` (bars), asking the scope for series. */
export function useMoveEvent() {
  const ui = useUi();
  const update = useUpdateEvent();
  return async (event: CalEvent, change: { deltaMinutes?: number; deltaDays?: number; newEnd?: string }) => {
    let changes: Partial<EventInput>;
    if (change.newEnd) {
      changes = { start: event.start, end: change.newEnd, allDay: event.allDay };
    } else if (event.allDay) {
      const d = change.deltaDays ?? 0;
      changes = { start: addDays(event.start, d), end: addDays(event.end, d), allDay: true };
    } else {
      const delta = (change.deltaMinutes ?? 0) + (change.deltaDays ?? 0) * 1440;
      changes = { start: fromAbsMinutes(absMinutes(event.start) + delta), end: fromAbsMinutes(absMinutes(event.end) + delta), allDay: false };
    }
    if (changes.start === event.start && changes.end === event.end) return;
    const scope = await ui.askScope(event, "edit");
    if (!scope) return;
    const previous = { start: event.start, end: event.end, allDay: event.allDay };
    update.mutate(
      { event, scope, changes },
      {
        onSuccess: (saved) => {
          if (scope === "this" || !event.isRecurring) {
            ui.showToast("Evento atualizado", {
              label: "Desfazer",
              run: () => update.mutate({ event: { ...saved, occurrenceStart: saved.occurrenceStart }, scope: "this", changes: previous })
            });
          } else {
            ui.showToast("Evento atualizado");
          }
        },
        onError: (err) => ui.showToast(`Não foi possível atualizar: ${(err as Error).message}`)
      }
    );
  };
}

export function HolidayPopover({ holiday, anchor, onClose }: { holiday: Holiday; anchor: Anchor; onClose: () => void }) {
  return (
    <Popover anchor={anchor} onClose={onClose} width={300}>
      <div style={{ padding: "20px 24px", display: "flex", gap: 16 }}>
        <span className="color-dot" style={{ background: HOLIDAY_COLOR, width: 14, height: 14, borderRadius: 4, marginTop: 6 }} />
        <div>
          <div style={{ fontSize: 22 }}>{holiday.name}</div>
          <div style={{ color: "var(--text-2)", marginTop: 4 }}>{longDateLabel(holiday.date)}</div>
          <div style={{ color: "var(--text-3)", marginTop: 12, fontSize: 13 }}>{holiday.national ? "Feriado nacional" : "Ponto facultativo"} · Feriados no Brasil</div>
        </div>
      </div>
    </Popover>
  );
}

export function useHolidayPopover() {
  const [state, setState] = useState<{ holiday: Holiday; anchor: Anchor } | null>(null);
  return {
    open: (holiday: Holiday, anchor: Anchor) => setState({ holiday, anchor }),
    element: state ? <HolidayPopover holiday={state.holiday} anchor={state.anchor} onClose={() => setState(null)} /> : null
  };
}
